import { useEffect, useMemo, useRef, useState } from 'react';

import { ButtonV2, ChevronLeftIcon, IconButton, ModalV2 as Modal, toast } from '@utilities';
import type { Question, ScreenerQuestionBank } from '@api/instant_survey/types';

import {
  buildScreenerValueMap,
  DEFAULT_TITLE,
  getHierarchyPromptKeys,
  getInitialScreenerPromptValues,
  getMissingScreenerPromptMessages,
  getScreenerPromptKeys,
  useScreenerQuestionBanks,
} from './utilities';
import BankView from './components/BankView';
import { renderTextWithBlankTokens } from './components/BankView/utilities/helpers';
import LibraryView from './components/LibraryView';
import PreviewView from './components/PreviewView';

import styles from './_index.module.scss';

interface CommonModalScreenerQuestionProps {
  onClose: () => void;
}

interface CreateModalScreenerQuestionProps extends CommonModalScreenerQuestionProps {
  initialQuestion?: never;
  mode: 'create';
  onSelectQuestion: (question: Question) => Promise<void>;
  onUpdateQuestion?: never;
}

interface EditModalScreenerQuestionProps extends CommonModalScreenerQuestionProps {
  initialQuestion: Question;
  mode: 'edit';
  onSelectQuestion?: never;
  onUpdateQuestion: (question: Question) => Promise<void>;
}

type ModalScreenerQuestionProps = CreateModalScreenerQuestionProps | EditModalScreenerQuestionProps;

const ModalScreenerQuestion = ({
  initialQuestion,
  mode,
  onClose,
  onSelectQuestion,
  onUpdateQuestion,
}: ModalScreenerQuestionProps) => {
  const isEditing = mode === 'edit';
  const {
    data: banks,
    isError: hasBanksError,
    isLoading: isLoadingBanks,
    refetch: refetchBanks,
  } = useScreenerQuestionBanks();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [bank, setBank] = useState<null | ScreenerQuestionBank>(null);
  const [previewQuestion, setPreviewQuestion] = useState<null | Question>(
    isEditing ? initialQuestion : null
  );
  const [promptValues, setPromptValues] = useState<Record<string, string>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const questionPromptKeys = useMemo(
    () => getScreenerPromptKeys(previewQuestion),
    [previewQuestion]
  );
  const hierarchyPromptKeys = useMemo(
    () => getHierarchyPromptKeys(previewQuestion),
    [previewQuestion]
  );
  const promptKeys = questionPromptKeys;
  const missingPromptKeys = useMemo(
    () => promptKeys.filter((promptKey) => !promptValues[promptKey]?.trim()),
    [promptKeys, promptValues]
  );
  const isSubmitDisabled = isSubmitting;

  const resetPromptState = (question: null | Question) => {
    const initialPromptValues = getInitialScreenerPromptValues(question);

    setPromptValues(initialPromptValues);
  };

  useEffect(() => {
    resetPromptState(previewQuestion);
    setHasAttemptedSubmit(false);
  }, [previewQuestion]);

  const handleBack = () => {
    if (previewQuestion) {
      setPreviewQuestion(null);
      return;
    }

    setBank(null);
  };

  const handleSubmit = async () => {
    if (!previewQuestion || isSubmittingRef.current) return;

    if (missingPromptKeys.length > 0) {
      setHasAttemptedSubmit(true);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const question = {
        ...previewQuestion,
        screenerValueMap: buildScreenerValueMap(promptKeys, promptValues),
      };

      if (mode === 'edit') {
        await onUpdateQuestion(question);
      } else {
        await onSelectQuestion(question);
      }
    } catch (error) {
      if (!isEditing) throw error;

      console.error('Error updating screener question', error);
      toast.error('Error updating question');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handlePromptChange = (promptKey: string, value: string) => {
    setPromptValues((currentPromptValues) => ({
      ...currentPromptValues,
      [promptKey]: value,
    }));
  };

  const titleTemplate =
    previewQuestion?.screenerLabel ||
    (isEditing ? previewQuestion?.text : bank?.name) ||
    DEFAULT_TITLE;
  const title = previewQuestion
    ? renderTextWithBlankTokens(titleTemplate, `preview-question-${previewQuestion.id}-title`)
    : titleTemplate;
  const isNestedView = Boolean(previewQuestion || bank);

  const actionButtons = previewQuestion
    ? [
        <ButtonV2
          disabled={isSubmitting}
          key="modal-cancel-button"
          onClick={onClose}
          shape="round"
          type="button"
          variant="default"
        >
          Cancel
        </ButtonV2>,
        <ButtonV2
          disabled={isSubmitDisabled}
          isLoading={isSubmitting}
          key="modal-confirm-button"
          onClick={handleSubmit}
          shape="round"
          type="button"
          variant="primary"
        >
          {isEditing ? 'Apply Changes' : 'Add to Survey'}
        </ButtonV2>,
      ]
    : [];
  const headerLeading =
    isNestedView && !isEditing ? (
      <IconButton
        aria-label="Back"
        className={styles['modal-screener-question-back']}
        onClick={handleBack}
        type="button"
      >
        <ChevronLeftIcon />
      </IconButton>
    ) : undefined;

  return (
    <Modal
      buttons={actionButtons}
      className={styles['modal-screener-question']}
      headerLeading={headerLeading}
      isActive
      isScrollable
      onClose={onClose}
      size="fullscreen"
      title={title}
    >
      {!bank && !previewQuestion && (
        <LibraryView
          banks={banks}
          isError={hasBanksError}
          isLoading={isLoadingBanks}
          onRetry={refetchBanks}
          onSelectBank={setBank}
        />
      )}
      {bank && !previewQuestion && <BankView bank={bank} onPreviewQuestion={setPreviewQuestion} />}
      {previewQuestion && (
        <>
          <PreviewView
            hierarchyPromptKeys={hierarchyPromptKeys}
            missingPromptKeys={hasAttemptedSubmit ? missingPromptKeys : []}
            missingPromptMessages={
              hasAttemptedSubmit
                ? getMissingScreenerPromptMessages(previewQuestion, missingPromptKeys)
                : []
            }
            onPromptChange={handlePromptChange}
            onResetPrompts={() => resetPromptState(previewQuestion)}
            previewQuestion={previewQuestion}
            promptValues={promptValues}
          />
        </>
      )}
    </Modal>
  );
};

export default ModalScreenerQuestion;
